import React, { useState, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { SearchSelect } from '../../search_select'
import GlgInlineLoadingSpinner from '../../../../shared/components/glg_loading_spinner_inline'
import {
  useGetCrsCoursesQuery,
  useGetScorecentreCoursesQuery,
  useGetGolfAdvisorCoursesQuery,
  useLoadCrsCourseTeesQuery,
  useLoadScorecentreCourseTeesQuery,
} from '../../../api/courses'

export const Step1CourseSearch = ({ source, onCourseSelect, selectedCourseId, onTeesSelect, selectedTeeIds }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const canUseCrdSpecificTeesImport = useSelector((state) => state?.misc?.data?.canUseCrdSpecificTeesImport)
  const showTeeSelector = source === 'crs' && canUseCrdSpecificTeesImport

  const { data: crsOptions, isLoading: crsLoading } = useGetCrsCoursesQuery(
    { q: searchQuery },
    { skip: source !== 'crs' || !searchQuery }
  )
  const { data: scorecentreOptions, isLoading: scorecentreLoading } = useGetScorecentreCoursesQuery(
    { q: searchQuery },
    { skip: source !== 'scorecentre' || !searchQuery }
  )
  const { data: golfAdvisorOptions, isLoading: golfAdvisorLoading } = useGetGolfAdvisorCoursesQuery(
    { q: searchQuery },
    { skip: source !== 'golf_advisor' || !searchQuery }
  )

  const { data: crsTees, isLoading: crsTeesLoading } = useLoadCrsCourseTeesQuery(
    { crs_course_id: selectedCourseId },
    { skip: source !== 'crs' || !selectedCourseId || !showTeeSelector }
  )
  const { data: scorecentreTees, isLoading: scorecentreTeesLoading } = useLoadScorecentreCourseTeesQuery(
    { scorecentre_course_id: selectedCourseId },
    { skip: source !== 'scorecentre' || !selectedCourseId }
  )

  const optionsBySource = { crs: crsOptions, scorecentre: scorecentreOptions, golf_advisor: golfAdvisorOptions }
  const loadingBySource = { crs: crsLoading, scorecentre: scorecentreLoading, golf_advisor: golfAdvisorLoading }
  const options = optionsBySource[source]
  const isLoading = loadingBySource[source]

  const teesDataBySource = { crs: crsTees, scorecentre: scorecentreTees }
  const teesLoadingBySource = { crs: crsTeesLoading, scorecentre: scorecentreTeesLoading }
  const activeTees = teesDataBySource[source]
  const teesLoading = teesLoadingBySource[source]

  const courseTeesKey = source === 'crs' ? 'crs_course_tees' : 'scorecentre_course_tees'
  const courseTees = useMemo(
    () => activeTees ? Object.entries(activeTees[courseTeesKey] || {}) : [],
    [activeTees, courseTeesKey]
  )

  const autoSelectedRef = useRef(false)

  useEffect(() => {
    autoSelectedRef.current = false
  }, [selectedCourseId])

  useEffect(() => {
    if (autoSelectedRef.current) return
    if (!courseTees.length) return
    autoSelectedRef.current = true
    onTeesSelect(courseTees.map(([id]) => String(id)))
  }, [courseTees, onTeesSelect])

  const handleTeeToggle = (teeId) => {
    const id = String(teeId)
    const updated = selectedTeeIds.includes(id)
      ? selectedTeeIds.filter((t) => t !== id)
      : [...selectedTeeIds, id]
    onTeesSelect(updated)
  }

  const handleCheckAll = () => {
    onTeesSelect(courseTees.map(([id]) => String(id)))
  }

  const handleUncheckAll = () => {
    onTeesSelect([])
  }

  return (
    <div>
      <div className="courses-modal-dropdown-label add-margin-top-10">
        {!window.I18n ? '' : window.I18n.t('courses.select_course_to_import')}
      </div>
      <SearchSelect
        placeholder={!window.I18n ? '' : window.I18n.t('courses.crs.popup.select_course')}
        handleSearchChange={setSearchQuery}
        handleSelectChange={onCourseSelect}
        options={options}
        isLoading={isLoading}
      />
      <div className="btsp_notices add-margin-top-20">
        {!window.I18n ? '' : window.I18n.t('courses.crs.popup.type_few_characters')}
      </div>

      {showTeeSelector && selectedCourseId && (
        <div className="add-margin-top-20" id="tees_for_import_container">
          <div className="courses-modal-dropdown-label">
            {!window.I18n ? '' : window.I18n.t('courses.crs.popup.select_tees_to_import')}
          </div>
          <div className="btsp_square_solid customer_box add-margin-top-10">
            {teesLoading && <GlgInlineLoadingSpinner show={true} />}
            {!teesLoading && (
              <>
                <div className="fieldset inline check add-margin-bottom-10">
                  <span className="courses-check-all-link" onClick={handleCheckAll}>
                    {!window.I18n ? '' : window.I18n.t('buttons.check_all')}
                  </span>
                  {' / '}
                  <span className="courses-check-all-link" onClick={handleUncheckAll}>
                    {!window.I18n ? '' : window.I18n.t('buttons.uncheck_all')}
                  </span>
                </div>
                <div className="row">
                  {courseTees.map(([id, tee]) => (
                    <div key={id} className="col-xs-6 fieldset inline check">
                      <label className="btsp_icheck_label no-padding-left">
                        <div className={`icheckbox_square-orange btsp-icheck-xs icheck-item icheck ${selectedTeeIds.includes(String(id)) ? 'checked' : ''}`}>
                          <input type="checkbox" className="icheck-input" checked={selectedTeeIds.includes(String(id))} onChange={() => handleTeeToggle(id)} />
                        </div>
                        <span>{tee.name} - {tee.gender}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

Step1CourseSearch.propTypes = {
  source: PropTypes.oneOf(['crs', 'scorecentre', 'golf_advisor']).isRequired,
  onCourseSelect: PropTypes.func.isRequired,
  selectedCourseId: PropTypes.string,
  onTeesSelect: PropTypes.func,
  selectedTeeIds: PropTypes.arrayOf(PropTypes.string),
}

Step1CourseSearch.defaultProps = {
  selectedTeeIds: [],
  onTeesSelect: () => {},
}
